import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import clsx from 'clsx';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectCurrentPageId } from '@/store/pages/selectors';
import {
  selectAnalyticsData,
  selectAnalyticsStatus,
  selectAnalyticsTimeRange,
  selectAnalyticsCustomRange,
  selectAnalyticsTraffic,
  selectAnalyticsMetrics,
} from '@/store/analytics/selectors';
import { fetchAnalytics } from '@/store/analytics/thunks';

import { AnalyticsSidebar } from '@/components/analytics/AnalyticsSidebar/AnalyticsSidebar';
import {
  AnalyticsChart,
  type ChartDataPoint,
} from '@/components/analytics/AnalyticsChart/AnalyticsChart';
import { AnalyticsMetricCard } from '@/components/analytics/AnalyticsMetricCard/AnalyticsMetricCard';
import {
  TableViewCard,
  type TableViewCardItem,
} from '@/components/analytics/TableViewCard/TableViewCard';
import { EngagementMetrics } from '@/components/analytics/EngagementMetrics/EngagementMetrics';
import { AnalyticsInspectorPanel } from '@/components/analytics/AnalyticsInspector/AnalyticsInspectorPanel';
import { LightDarkMode } from '@/components/widgets/LightDarkMode/LightDarkMode';
import type { EngagementMetric, FilterItem, TimeRange } from '@/components/analytics/types';
import type { CardMetrics } from '@/store/analytics/types';

import IconFacebook from '@/assets/icons/icons-social/icon-facebook.svg?react';
import IconInstagram from '@/assets/icons/icons-social/icon-instagram.svg?react';
import IconGoogle from '@/assets/icons/icons-social/icon-google.svg?react';
import IconLinkedIn from '@/assets/icons/icons-social/icon-linkedIn.svg?react';
import IconYoutube from '@/assets/icons/icons-social/icon-youtube.svg?react';
import IconMedium from '@/assets/icons/icons-social/icon-medium.svg?react';
import IconSlash from '@/assets/icons/icon-slash.svg?react';
import IconChrome from '@/assets/icons/icons-browser/icon-chrome.svg?react';
import IconFirefox from '@/assets/icons/icons-browser/icon-firefox.svg?react';
import IconSafari from '@/assets/icons/icons-browser/icon-safari.svg?react';
import IconEdge from '@/assets/icons/icons-browser/icon-edge.svg?react';
import IconOther from '@/assets/icons/icons-browser/icon-other.svg?react';
import IconMobile from '@/assets/icons/icon-mobile.svg?react';
import IconDesktop from '@/assets/icons/icon-desktop.svg?react';
import IconTablet from '@/assets/icons/icon-tablet.svg?react';

/* ─────────────────────────── icon helpers ─────────────────────────── */

const SOCIAL_ICONS: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  facebook: IconFacebook,
  instagram: IconInstagram,
  google: IconGoogle,
  linkedin: IconLinkedIn,
  youtube: IconYoutube,
  medium: IconMedium,
};

const getSocialIcon = (domain: string): React.ReactNode => {
  const key = Object.keys(SOCIAL_ICONS).find((k) => domain.toLowerCase().includes(k));
  if (key) {
    const Icon = SOCIAL_ICONS[key];
    return <Icon className="h-4 w-4" />;
  }
  return <div className="h-4 w-4 rounded-full bg-neutral-200" />;
};

const getUTMIcon = (): React.ReactNode => <IconSlash className="h-4 w-4 text-neutral-400" />;

const COUNTRY_CODES: Record<string, string> = {
  'United States': 'US',
  'United Kingdom': 'GB',
  Canada: 'CA',
  Germany: 'DE',
  Australia: 'AU',
  France: 'FR',
  Brazil: 'BR',
  Japan: 'JP',
  India: 'IN',
  China: 'CN',
  Mexico: 'MX',
  Italy: 'IT',
  Spain: 'ES',
  Netherlands: 'NL',
  Sweden: 'SE',
  Norway: 'NO',
  Denmark: 'DK',
  Finland: 'FI',
  Poland: 'PL',
  Ireland: 'IE',
  'South Korea': 'KR',
  Argentina: 'AR',
  Colombia: 'CO',
  Portugal: 'PT',
  Switzerland: 'CH',
  Austria: 'AT',
  Belgium: 'BE',
  'New Zealand': 'NZ',
  Singapore: 'SG',
  'South Africa': 'ZA',
  Israel: 'IL',
  Turkey: 'TR',
  Russia: 'RU',
  Ukraine: 'UA',
  Philippines: 'PH',
  Thailand: 'TH',
  Indonesia: 'ID',
  Malaysia: 'MY',
  Vietnam: 'VN',
  Nigeria: 'NG',
  Egypt: 'EG',
  Chile: 'CL',
  Peru: 'PE',
  Romania: 'RO',
  Czechia: 'CZ',
  Greece: 'GR',
  Hungary: 'HU',
};

const countryToEmoji = (country: string): string => {
  const code = COUNTRY_CODES[country];
  if (!code) return '\uD83C\uDFF3\uFE0F';
  return String.fromCodePoint(...code.split('').map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
};

const getFlagIcon = (country: string): React.ReactNode => (
  <span className="text-sm leading-none" role="img" aria-label={country}>
    {countryToEmoji(country)}
  </span>
);

const BROWSER_ICONS: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  chrome: IconChrome,
  firefox: IconFirefox,
  safari: IconSafari,
  edge: IconEdge,
};

const DEVICE_ICONS: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  mobile: IconMobile,
  desktop: IconDesktop,
  tablet: IconTablet,
};

const getDeviceIcon = (name: string): React.ReactNode => {
  const key = name.toLowerCase();
  if (key in DEVICE_ICONS) {
    const Icon = DEVICE_ICONS[key];
    return <Icon className="h-4 w-4 text-neutral-400" />;
  }
  return <div className="h-4 w-4 rounded-full bg-neutral-200" />;
};

const getBrowserIcon = (name: string): React.ReactNode => {
  const key = name.toLowerCase();
  if (key in BROWSER_ICONS) {
    const Icon = BROWSER_ICONS[key];
    return <Icon className="h-4 w-4" />;
  }
  return <IconOther className="h-4 w-4 text-neutral-400" />;
};

/* ─────────────────────────── helpers ─────────────────────────── */

function periodLabel(timeRange: TimeRange, customRange: unknown): string {
  if (customRange) return 'vs previous period';
  if (timeRange === '7 Days') return 'vs previous 7 days';
  if (timeRange === '90 Days') return 'vs previous 90 days';
  return 'vs previous 30 days';
}

function aggregateWeekly(dailyData: ChartDataPoint[]): ChartDataPoint[] {
  const result: ChartDataPoint[] = [];

  for (let i = 0; i < dailyData.length; i += 7) {
    const chunk = dailyData.slice(i, i + 7);
    const startLabel = chunk[0].date;
    const endLabel = chunk[chunk.length - 1].date;

    result.push({
      date: chunk.length > 1 ? `${startLabel} – ${endLabel}` : startLabel,
      views: chunk.reduce((sum, d) => sum + (d.views ?? 0), 0),
      uv: chunk.reduce((sum, d) => sum + (d.uv ?? 0), 0),
      cv: chunk.reduce((sum, d) => sum + (d.cv ?? 0), 0),
    });
  }

  return result;
}

function cardMetricsToRow(cm: CardMetrics): {
  views: number;
  uv: number;
  cv: number;
  cvr: number;
} {
  return {
    views: cm.views,
    uv: cm.unique_visitors,
    cv: cm.conversions,
    cvr: cm.cvr,
  };
}

/* ─────────────────────────── types ─────────────────────────── */

type WidgetId = 'sources' | 'utm' | 'geo' | 'browser' | 'devices' | 'engagement';

/* ─────────────────────────── component ─────────────────────────── */

interface AnalyticsPageProps {
  navbar: React.ReactNode;
}

export const AnalyticsPage: React.FC<AnalyticsPageProps> = ({ navbar }) => {
  const dispatch = useAppDispatch();
  const pageId = useAppSelector(selectCurrentPageId);
  const status = useAppSelector(selectAnalyticsStatus);
  const data = useAppSelector(selectAnalyticsData);
  const timeRange = useAppSelector(selectAnalyticsTimeRange);
  const customRange = useAppSelector(selectAnalyticsCustomRange);

  /* ── Widget / filter state ── */
  const [selectedWidget, setSelectedWidget] = useState<WidgetId | null>(null);
  const [selectedItems, setSelectedItems] = useState<Map<string, FilterItem>>(new Map());
  const traffic = useAppSelector(selectAnalyticsTraffic);
  const metrics = useAppSelector(selectAnalyticsMetrics);

  useEffect(() => {
    if (!pageId) return;
    dispatch(fetchAnalytics(pageId));
  }, [dispatch, pageId, timeRange, customRange]);

  /* ── Widget selection ── */
  const handleWidgetSelect = (id: WidgetId) => {
    setSelectedWidget((prev) => (prev === id ? null : id));
  };

  /* ── Item (row) selection → sidebar filters ── */
  const handleItemSelect = useCallback(
    (category: string, name: string, sel: boolean, iconFn: () => React.ReactNode) => {
      setSelectedItems((prev) => {
        const next = new Map(prev);
        const key = `${category}:${name}`;
        if (sel) {
          next.set(key, { id: key, label: name, icon: iconFn() });
        } else {
          next.delete(key);
        }
        return next;
      });
    },
    [],
  );

  const handleRemoveFilter = useCallback((id: string) => {
    setSelectedItems((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const handleClearFilters = useCallback(() => {
    setSelectedItems(new Map());
  }, []);

  /* ── Derived values ── */
  const filters = useMemo(() => Array.from(selectedItems.values()), [selectedItems]);
  const toggleState: Record<string, boolean> = { ...traffic, ...metrics };

  const sourcesSelected = useMemo(
    () =>
      new Set(
        Array.from(selectedItems.keys())
          .filter((k) => k.startsWith('sources:'))
          .map((k) => k.replace('sources:', '')),
      ),
    [selectedItems],
  );

  const utmSelected = useMemo(
    () =>
      new Set(
        Array.from(selectedItems.keys())
          .filter((k) => k.startsWith('utm:'))
          .map((k) => k.replace('utm:', '')),
      ),
    [selectedItems],
  );

  const geoSelected = useMemo(
    () =>
      new Set(
        Array.from(selectedItems.keys())
          .filter((k) => k.startsWith('geo:'))
          .map((k) => k.replace('geo:', '')),
      ),
    [selectedItems],
  );

  const browserSelected = useMemo(
    () =>
      new Set(
        Array.from(selectedItems.keys())
          .filter((k) => k.startsWith('browser:'))
          .map((k) => k.replace('browser:', '')),
      ),
    [selectedItems],
  );

  const devicesSelected = useMemo(
    () =>
      new Set(
        Array.from(selectedItems.keys())
          .filter((k) => k.startsWith('devices:'))
          .map((k) => k.replace('devices:', '')),
      ),
    [selectedItems],
  );

  const isInspectorOpen = selectedWidget !== null;

  // Keep inspector content visible during close animation
  const lastWidgetRef = useRef<WidgetId | null>(null);
  const [showInspector, setShowInspector] = useState(false);

  useEffect(() => {
    if (selectedWidget !== null) {
      lastWidgetRef.current = selectedWidget;
      setShowInspector(true);
    } else {
      const timer = setTimeout(() => setShowInspector(false), 300);
      return () => clearTimeout(timer);
    }
  }, [selectedWidget]);

  const displayWidget = selectedWidget ?? lastWidgetRef.current;

  const isLoading = status === 'loading' || status === 'idle';
  const label = periodLabel(timeRange, customRange);

  /* ── Chart data ── */
  const chartData: ChartDataPoint[] = useMemo(() => {
    if (!data) return [];

    const dailyData = data.breakdown_labels.map((date, i) => ({
      date,
      views: data.views_breakdown[i],
      uv: data.unique_visitors_breakdown[i],
      cv: data.conversions_breakdown[i],
    }));

    if (timeRange === '90 Days' && !customRange) {
      return aggregateWeekly(dailyData);
    }

    return dailyData;
  }, [data, timeRange, customRange]);

  /* ── Metric cards (always defined, populated from data when available) ── */
  const metricCardsData = [
    {
      label: 'Unique Visitors',
      value: data?.unique_visitors ?? null,
      previousValue: data?.previous_period_unique_visitors ?? null,
      toggleKey: 'uniqueVisitors' as const,
    },
    {
      label: 'Total Views',
      value: data?.views ?? null,
      previousValue: data?.previous_period_views ?? null,
      toggleKey: 'views' as const,
    },
    {
      label: 'Conversions',
      value: data?.conversions ?? null,
      previousValue: data?.previous_period_conversions ?? null,
      toggleKey: 'conversions' as const,
    },
    {
      label: 'Conv Rate',
      value: data?.conversion_rate ?? null,
      previousValue: data?.previous_period_conversion_rate ?? null,
      toggleKey: 'conversionRate' as const,
      formatValue: (v: number) => `${v.toFixed(1)}%`,
      formatDelta: (d: number) => `${d.toFixed(1)}%`,
    },
    {
      label: 'Bounce Rate',
      value: data?.bounce_rate ?? null,
      previousValue: data?.previous_period_bounce_rate ?? null,
      toggleKey: 'bounceRate' as const,
      invertTrend: true,
      formatValue: (v: number) => `${v.toFixed(1)}%`,
      formatDelta: (d: number) => `${d.toFixed(1)}%`,
    },
    {
      label: 'Avg Session',
      value: data?.avg_session_seconds ?? null,
      previousValue: data?.previous_period_avg_session_seconds ?? null,
      toggleKey: null,
      formatValue: (v: number) => `${Math.floor(v / 60)}m ${v % 60}s`,
    },
  ];

  const visibleMetricCards = metricCardsData.filter((card) =>
    card.toggleKey ? (toggleState[card.toggleKey] ?? true) : true,
  );

  /* ── Table card items ── */
  const sourcesItems: TableViewCardItem[] = data
    ? Object.entries(data.sources).map(([domain, cm]) => ({
        key: domain,
        label: domain,
        icon: getSocialIcon(domain),
        ...cardMetricsToRow(cm),
      }))
    : [];

  const utmItems: TableViewCardItem[] = data
    ? Object.entries(data.campaign_utm).map(([name, cm]) => ({
        key: name,
        label: name,
        icon: getUTMIcon(),
        ...cardMetricsToRow(cm),
      }))
    : [];

  const geoItems: TableViewCardItem[] = data
    ? Object.entries(data.country).map(([country, cm]) => ({
        key: country,
        label: country,
        icon: getFlagIcon(country),
        ...cardMetricsToRow(cm),
      }))
    : [];

  const browserItems: TableViewCardItem[] = data
    ? Object.entries(data.browser).map(([name, cm]) => ({
        key: name,
        label: name,
        icon: getBrowserIcon(name),
        ...cardMetricsToRow(cm),
      }))
    : [];

  const deviceItems: TableViewCardItem[] = data
    ? Object.entries(data.device).map(([name, cm]) => ({
        key: name,
        label: name,
        icon: getDeviceIcon(name),
        ...cardMetricsToRow(cm),
      }))
    : [];

  /* ── Engagement ── */
  const engagementMetrics: EngagementMetric[] | undefined = data
    ? [
        {
          label: 'Bounce Rate',
          value: `${data.engagement.bounce_rate.toFixed(1)}%`,
        },
        {
          label: 'New Visitors',
          value: data.engagement.new_visitors.toLocaleString(),
        },
        {
          label: 'Returning Visitors',
          value: data.engagement.returning_visitors.toLocaleString(),
        },
      ]
    : undefined;

  /* ── Inspector data for the selected widget ── */
  const inspectorData: Record<
    WidgetId,
    { title: string; sections: React.ComponentProps<typeof AnalyticsInspectorPanel>['sections'] }
  > = useMemo(() => {
    const mapEntries = (
      entries: Record<string, CardMetrics>,
    ): Array<{ name: string; rows: Array<{ label: string; value: string | number }> }> =>
      Object.entries(entries).map(([name, cm]) => ({
        name,
        rows: [
          { label: 'Views', value: cm.views.toLocaleString() },
          { label: 'Unique Visitors', value: cm.unique_visitors.toLocaleString() },
          { label: 'Conversions', value: cm.conversions.toLocaleString() },
          { label: 'Conv. Rate', value: `${cm.cvr.toFixed(1)}%` },
        ],
      }));

    return {
      sources: {
        title: 'Sources',
        sections: [{ rows: [], items: data ? mapEntries(data.sources) : [] }],
      },
      utm: {
        title: 'Campaign UTM',
        sections: [{ rows: [], items: data ? mapEntries(data.campaign_utm) : [] }],
      },
      geo: {
        title: 'Geographic Data',
        sections: [{ rows: [], items: data ? mapEntries(data.country) : [] }],
      },
      browser: {
        title: 'Browser',
        sections: [{ rows: [], items: data ? mapEntries(data.browser) : [] }],
      },
      devices: {
        title: 'Devices',
        sections: [{ rows: [], items: data ? mapEntries(data.device) : [] }],
      },
      engagement: {
        title: 'Engagement',
        sections: [
          {
            title: 'Metrics',
            rows: engagementMetrics
              ? engagementMetrics.map((m) => ({ label: m.label, value: String(m.value) }))
              : [],
          },
        ],
      },
    };
  }, [data, engagementMetrics]);

  return (
    <div className="flex h-screen w-full flex-col bg-neutral-100">
      {/* Navbar */}
      <div className="shrink-0">{navbar}</div>

      {/* Main layout: sidebar + content + inspector */}
      <div className="flex min-h-0 flex-1">
        {/* Sidebar */}
        <aside className="shrink-0 overflow-y-auto [scrollbar-width:none]">
          <AnalyticsSidebar
            filters={filters}
            onRemoveFilter={handleRemoveFilter}
            onClearFilters={handleClearFilters}
          />
        </aside>

        {/* Content area */}
        <main className="relative flex min-w-0 flex-1 flex-col overflow-y-auto px-6 pt-6 [scrollbar-width:none]">
          <div className="mx-auto flex w-full max-w-250 flex-col gap-6 pb-6">
            {/* Overview headline */}
            <section className="flex w-full flex-col items-start gap-2">
              <span className="text-xs leading-5 font-semibold text-indigo-500">Overview</span>
              <p className="text-xl leading-8 font-normal text-neutral-900 sm:text-2xl sm:leading-10">
                Since your last optimisation,{' '}
                <span className="font-bold">CTA clicks increased by 14%</span>
              </p>
            </section>

            {/* Metric cards */}
            <section className="grid grid-cols-2 gap-3 lg:auto-cols-fr lg:grid-flow-col lg:grid-cols-none">
              {visibleMetricCards.map((card) => (
                <AnalyticsMetricCard
                  key={card.label}
                  label={card.label}
                  value={card.value}
                  previousValue={card.previousValue}
                  periodLabel={label}
                  formatValue={card.formatValue}
                  formatDelta={card.formatDelta}
                  invertTrend={card.invertTrend}
                />
              ))}
            </section>

            {/* Chart */}
            <section className="w-full">
              <AnalyticsChart
                data={chartData}
                isLoading={isLoading}
                traffic={traffic}
                publishTimestamps={data?.publish_timestamps ?? []}
              />
            </section>

            {/* Widget grid — row 1 */}
            <div className="grid grid-cols-1 items-stretch gap-4 *:min-w-0 md:grid-cols-2">
              <TableViewCard
                title={
                  <span className="text-xs leading-5 font-semibold text-neutral-900">Sources</span>
                }
                items={sourcesItems}
                emptyStateMessage="No Source data available"
                searchable
                searchLabel="Search sources"
                sortable
                selected={selectedWidget === 'sources'}
                onSelect={() => handleWidgetSelect('sources')}
                selectedItems={sourcesSelected}
                onItemSelect={(domain, sel) =>
                  handleItemSelect('sources', domain, sel, () => getSocialIcon(domain))
                }
              />
              <TableViewCard
                title={
                  <span className="text-xs leading-5 font-semibold text-neutral-900">
                    Campaign UTM
                  </span>
                }
                items={utmItems}
                emptyStateMessage="No UTM sources yet"
                searchable
                searchLabel="Search UTM campaigns"
                sortable
                selected={selectedWidget === 'utm'}
                onSelect={() => handleWidgetSelect('utm')}
                selectedItems={utmSelected}
                onItemSelect={(name, sel) => handleItemSelect('utm', name, sel, () => getUTMIcon())}
              />
            </div>

            {/* Widget grid — row 2 */}
            <div className="grid grid-cols-1 items-stretch gap-4 *:min-w-0 md:grid-cols-2">
              <TableViewCard
                title={
                  <span className="text-xs leading-5 font-semibold text-neutral-900">
                    Geographic Data
                  </span>
                }
                items={geoItems}
                emptyStateMessage="No Geographic data yet"
                searchable
                searchLabel="Search geographic data"
                sortable
                selected={selectedWidget === 'geo'}
                onSelect={() => handleWidgetSelect('geo')}
                selectedItems={geoSelected}
                onItemSelect={(country, sel) =>
                  handleItemSelect('geo', country, sel, () => getFlagIcon(country))
                }
              />
              <TableViewCard
                title={
                  <span className="text-xs leading-5 font-semibold text-neutral-900">Browser</span>
                }
                items={browserItems}
                emptyStateMessage="No Browser data available"
                searchable
                searchLabel="Search browsers"
                sortable
                selected={selectedWidget === 'browser'}
                onSelect={() => handleWidgetSelect('browser')}
                selectedItems={browserSelected}
                onItemSelect={(name, sel) =>
                  handleItemSelect('browser', name, sel, () => getBrowserIcon(name))
                }
              />
            </div>

            {/* Widget grid — row 3 */}
            <div className="grid grid-cols-1 items-stretch gap-4 *:min-w-0 md:grid-cols-2">
              <TableViewCard
                title={
                  <span className="text-xs leading-5 font-semibold text-neutral-900">Devices</span>
                }
                items={deviceItems}
                emptyStateMessage="No Devices data yet"
                searchable
                searchLabel="Search devices"
                sortable
                selected={selectedWidget === 'devices'}
                onSelect={() => handleWidgetSelect('devices')}
                selectedItems={devicesSelected}
                onItemSelect={(name, sel) =>
                  handleItemSelect('devices', name, sel, () => getDeviceIcon(name))
                }
              />
              <EngagementMetrics
                metrics={engagementMetrics}
                selected={selectedWidget === 'engagement'}
                onSelect={() => handleWidgetSelect('engagement')}
              />
            </div>
          </div>

          <div
            className={clsx(
              'bg-neutral-0 sticky bottom-5 z-10 mx-auto mt-4 inline-flex items-center justify-start rounded-xl px-2 py-2 shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)] outline -outline-offset-1 outline-neutral-100',
            )}
          >
            <LightDarkMode />
          </div>
        </main>

        {/* Inspector panel */}
        <aside
          className={clsx(
            'shrink-0 overflow-hidden transition-[width] duration-300 ease-out',
            isInspectorOpen ? 'w-60' : 'w-0',
          )}
        >
          <div
            className={clsx(
              'h-full w-60 transition-transform duration-300 ease-out',
              isInspectorOpen ? 'translate-x-0' : 'translate-x-full',
            )}
          >
            {showInspector && displayWidget && (
              <AnalyticsInspectorPanel
                title={inspectorData[displayWidget].title}
                sections={inspectorData[displayWidget].sections}
                onClose={() => setSelectedWidget(null)}
              />
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};
